import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Turnstile, SEO } from '@daycal/ui';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.jsx';
import { FiCalendar, FiUser, FiMail, FiLock, FiClock, FiGlobe, FiCheckCircle, FiShield, FiAlertCircle, FiEye, FiEyeOff, FiX } from 'react-icons/fi';

export default function Signup() {
  const { signup, verifySignup, resendSignupOtp } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', confirmPassword: '' });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Two-phase signup: 'form' collects details, 'verify' enters the emailed 6-digit code.
  const [phase, setPhase] = useState('form');
  const [code, setCode] = useState('');
  const [resendMsg, setResendMsg] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const cycle = setInterval(() => {
      setActiveStep((s) => (s + 1) % 4);
    }, 2000);
    return () => clearInterval(cycle);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await signup({ ...form, turnstile_token: turnstileToken });
      setPhase('verify'); // account created (unconfirmed) → ask for the emailed code
    } catch (err) {
      setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifySignup(form.email, code.trim(), form.password);
      nav('/dashboard');
    } catch (err) {
      setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setResendMsg('');
    try {
      await resendSignupOtp(form.email);
      setResendMsg('A new code is on its way to your email.');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <SEO title="Get Started - DayCal" path="/signup" />
      
      <div className="grid w-full grid-cols-1 lg:grid-cols-12">
        {/* Left Side: Signup Form (5 cols on lg) */}
        <div className="flex items-center justify-center px-6 py-12 sm:px-16 lg:col-span-5 bg-white relative z-10 shadow-xl lg:order-last">
          {/* Close button returning to home */}
          <Link
            to="/"
            title="Return to Home"
            className="absolute top-5 right-5 sm:top-6 sm:right-6 grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors z-20"
          >
            <FiX className="h-5 w-5" />
          </Link>

          <div className="w-full max-w-[400px] animate-dc-rise">
            
            {/* Header logo / back link */}
            <div className="mb-8 flex flex-col items-center justify-center text-center">
              <Link to="/" className="group mb-5 flex flex-col items-center">
                <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-20 w-auto -my-4 object-contain transition-transform duration-300 group-hover:scale-105" />
              </Link>
              {phase === 'form' ? (
                <>
                  <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('auth.signUpTitle')}</h1>
                  <p className="mt-2 text-sm text-slate-500">Create your free scheduling account instantly</p>
                </>
              ) : (
                <>
                  <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Verify your email</h1>
                  <p className="mt-2 text-sm text-slate-500">
                    We sent a 6-digit code to <span className="font-semibold text-slate-700">{form.email}</span>
                  </p>
                </>
              )}
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 p-4 text-red-600 animate-pulse">
                <FiAlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0" />
                <span className="text-sm font-semibold leading-relaxed">{error}</span>
              </div>
            )}

            {resendMsg && phase === 'verify' && (
              <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-700">
                <FiCheckCircle className="mt-0.5 h-[18px] w-[18px] shrink-0" />
                <span className="text-sm font-semibold leading-relaxed">{resendMsg}</span>
              </div>
            )}

            {phase === 'verify' && (
              <form onSubmit={verify} className="space-y-4">
                <div>
                  <label htmlFor="code" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                    Verification code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    placeholder="000000"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-[0.5em] outline-none transition-all focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="w-full rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-slate-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20"
                >
                  {busy ? 'Verifying...' : 'Verify & continue'}
                </button>

                <div className="text-center text-sm font-medium text-slate-500">
                  Didn't get the code?{' '}
                  <button type="button" onClick={resend} className="font-bold text-slate-900 hover:underline">
                    Resend
                  </button>
                </div>
              </form>
            )}

            {phase === 'form' && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {t('auth.fullName')}
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiUser className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={set('name')}
                    required
                    placeholder="John Doe"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-4 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="username" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {t('auth.username')}
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiGlobe className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="username"
                    type="text"
                    value={form.username}
                    onChange={set('username')}
                    required
                    placeholder="johndoe"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-4 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiMail className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    required
                    placeholder="john@example.com"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-4 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiLock className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    required
                    minLength={8}
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-10 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
                  />
                  <button
                    type="button"
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <FiEyeOff className="h-4.5 w-4.5" /> : <FiEye className="h-4.5 w-4.5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Confirm Password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FiLock className="h-4.5 w-4.5" />
                  </span>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={set('confirmPassword')}
                    required
                    minLength={8}
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-slate-400 bg-white hover:border-slate-500 pl-10 pr-10 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
                  />
                  <button
                    type="button"
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <FiEyeOff className="h-4.5 w-4.5" /> : <FiEye className="h-4.5 w-4.5" />}
                  </button>
                </div>
              </div>

              <div className="py-2">
                <Turnstile siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY} onToken={setTurnstileToken} />
              </div>

              <button
                type="submit"
                disabled={busy || !turnstileToken}
                className="w-full rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-slate-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20"
              >
                {busy ? 'Creating Account...' : t('auth.createFreeAccount')}
              </button>
            </form>
            )}

            {phase === 'form' ? (
              <div className="mt-8 text-center text-sm font-medium text-slate-500">
                Have an account? <Link to="/login" className="font-bold text-slate-900 hover:underline">{t('auth.signIn')}</Link>
              </div>
            ) : (
              <div className="mt-8 text-center text-sm font-medium text-slate-500">
                Wrong email?{' '}
                <button
                  type="button"
                  onClick={() => { setPhase('form'); setCode(''); setError(''); setResendMsg(''); }}
                  className="font-bold text-slate-900 hover:underline"
                >
                  Go back
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Animated Scheduling Showcase (7 cols on lg, hidden on mobile) */}
        <div className="hidden lg:flex lg:col-span-7 bg-slate-950 text-white relative overflow-hidden items-center justify-center p-12 lg:order-first">
          {/* Decorative background grid and gradients */}
          <div className="absolute inset-0 bg-dot-grid opacity-30" />
          <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-brand-500/10 blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-indigo-500/10 blur-[100px]" />

          {/* Floating scheduling elements */}
          <div className="absolute top-16 right-16 animate-dc-float opacity-30 [animation-delay:1s]">
            <FiCalendar className="h-12 w-12 text-brand-400" />
          </div>
          <div className="absolute bottom-12 left-20 animate-dc-float opacity-30 [animation-delay:3s]">
            <FiClock className="h-10 w-10 text-indigo-400" />
          </div>
          <div className="absolute bottom-1/3 right-16 animate-dc-float opacity-20 [animation-delay:5s]">
            <FiShield className="h-8 w-8 text-emerald-400" />
          </div>

          {/* Main Visual content container */}
          <div className="relative z-10 w-full max-w-[500px]">
            <h2 className="text-3xl font-extrabold tracking-tight text-white mb-3">
              One link, endless possibilities.
            </h2>
            <p className="text-slate-400 text-sm mb-12">
              Share your custom DayCal link and let clients book appointments directly into your free slots.
            </p>

            {/* Simulated Availability and Setup Widget */}
            <div className="space-y-4">
              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 0 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 0 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiUser className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">1. Secure Your Username</p>
                    <p className="text-xs text-slate-400 mt-0.5">Get your custom link: <span className="text-brand-400">daycal.in/u/username</span></p>
                  </div>
                  {activeStep > 0 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 1 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 1 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiClock className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">2. Define Availability</p>
                    <p className="text-xs text-slate-400 mt-0.5">Configure your weekly working hours and break durations.</p>
                  </div>
                  {activeStep > 1 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 2 
                  ? 'border-brand-500/50 bg-slate-900 shadow-lg shadow-brand-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 2 ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiCalendar className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">3. Accept Free & Paid Bookings</p>
                    <p className="text-xs text-slate-400 mt-0.5">Collect payments instantly via Stripe or Razorpay.</p>
                  </div>
                  {activeStep > 2 && <FiCheckCircle className="text-emerald-500 h-5 w-5" />}
                </div>
              </div>

              <div className={`p-5 rounded-2xl border transition-all duration-500 ${
                activeStep === 3 
                  ? 'border-emerald-500/50 bg-slate-900 shadow-lg shadow-emerald-500/5 scale-102' 
                  : 'border-slate-800 bg-slate-900/40 opacity-60'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-colors ${
                    activeStep === 3 ? 'bg-emerald-600 text-white animate-pulse' : 'bg-slate-800 text-slate-400'
                  }`}>
                    <FiCheckCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">4. Enjoy Smooth Automation</p>
                    <p className="text-xs text-slate-400 mt-0.5">No more back-and-forth emails. Simply share your link.</p>
                  </div>
                  {activeStep === 3 && <span className="text-xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full animate-pulse">Active</span>}
                </div>
              </div>
            </div>

            {/* Bottom info banner */}
            <div className="mt-10 flex items-center gap-3 bg-indigo-950/40 border border-indigo-900/50 p-4 rounded-2xl">
              <FiShield className="h-5 w-5 text-indigo-400 shrink-0" />
              <p className="text-xs text-indigo-200">
                DayCal protects calendar integrity and strictly respects your privacy.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
